import 'yet-another-react-lightbox/styles.css'

import { Button } from '@calizahq/react-components-v2'
import React, { FC, Fragment, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Lightbox from 'yet-another-react-lightbox'

import { BottomSheet, Tag } from '@/components'
import { useMobile, useToggle } from '@/hooks'
import { Priority, Status, TicketDetail, Type } from '@/types/Ticket'
import { formatDate, parseDate } from '@/util'

import { OverviewRow } from '../OverviewRow'
import { SectionContainer } from '../SectionContainer'
import { parseAndFormatDate } from '../Ticket.util'
import { TicketDetailTab } from '../TicketDetailTabs'
import { ChildTicketsSection } from './ChildTicketsSection'
import { DescriptionField } from './DescriptionField'
import { OwnerSelection } from './OwnerSelection'
import { ReasonSelection } from './ReasonSelection'
import { TicketStatusSelection } from './StatusSelection'
import { TypeSelection } from './TypeSelection'

interface DetailsSectionProps {
  handleCloseTicket: () => void
  refetchDetails: () => void
  selectedTab: TicketDetailTab
  ticketData: TicketDetail
}

export const DetailsSection: FC<DetailsSectionProps> = ({
  selectedTab,
  refetchDetails,
  handleCloseTicket,
  ticketData,
}) => {
  const { isMobile } = useMobile()
  const navigate = useNavigate()
  const { isShown: modalShown, toggle: modalToggle } = useToggle()
  const { isShown: descriptionModalShown, toggle: descriptionModalToggle } = useToggle()
  const {
    description,
    attachments,
    reservation,
    priority,
    marketSegment,
    id: ticketId,
    ticketReason,
    createdAt,
    closedAt,
    home,
    contact,
    internalTicketStatus,
    ticketType,
    assignee,
    creator,
    ticketTypeId,
    ticketReasonId,
    ticketStatusId,
    ticketHomeArea,
    permissionToEnter,
    externalId,
    externalSource,
    parentTicket,
    parentTicketId,
    childTickets,
  } = ticketData

  const images =
    attachments && attachments.length
      ? attachments.filter((attachment) => attachment.contentType.includes('image'))
      : []

  const [photoIndex, setPhotoIndex] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const getPriorityTagVariant = () => {
    if (priority === Priority.HIGH) return 'error'
    else if (priority === Priority.MEDIUM) return 'warning'
    else return 'info'
  }

  const showImage = (index: number) => {
    setPhotoIndex(index)
    setIsOpen(true)
  }

  const { landingDirect, directPartnerUser } = home?.property || {}

  const renderCasePriority = () => (
    <Tag size="regular" variant={getPriorityTagVariant()}>
      {priority}
    </Tag>
  )

  const renderBooleanLabel = (status: boolean) => (
    <Tag size="regular" variant={status ? 'info' : 'warning'}>
      {status ? 'Yes' : 'No'}
    </Tag>
  )

  const renderMarketSegment = () => {
    if (!marketSegment) return null

    return (
      <div className="flex items-center justify-center rounded-lg border px-3 py-1 font-bold text-p-sm border-secondary">
        {marketSegment.name}
      </div>
    )
  }

  const navigateToHome = (id) => {
    id && navigate(`/homes/${id}`)
  }

  const navigateToReservation = (id) => {
    id && navigate(`/reservations/${id}`)
  }

  const navigateToMember = (id) => {
    id && navigate(`/members/${id}`)
  }

  if (isMobile && selectedTab !== TicketDetailTab.Details) return null
  const isTicketClosed = internalTicketStatus === Status.CLOSED

  return (
    <SectionContainer className="w-full md:mr-6 md:w-104">
      <p className="mb-6 hidden text-header-md md:block" style={{ fontWeight: 700 }}>
        Overview
      </p>

      <p
        className="mb-6 leading-7 underline"
        onClick={() => !isTicketClosed && descriptionModalToggle()}
        role="presentation"
        style={{ overflowWrap: 'break-word' }}
      >
        {description}
      </p>

      {!!images && !!images.length && (
        <Fragment>
          <p className="font-weight[700] mb-4 " style={{ fontWeight: 700 }}>
            Uploaded files
          </p>
          <div className="relative mb-6 flex flex-row justify-between">
            {images.length > 3 && (
              <div className="absolute right-2 top-2 flex  h-8 w-8 items-center justify-center rounded-full border border-white bg-action-input">
                <div className="leading-1 text-label-xs text-white">+{images.length - 3}</div>
              </div>
            )}
            <div className="grid grid-cols-3 grid-rows-1 place-items-stretch gap-3 ">
              {images.map((file, i) => {
                if (i > 2) return
                return (
                  <img
                    alt={file.filename}
                    aria-hidden="true"
                    className="h-30 w-30 overflow-hidden rounded-lg"
                    key={i}
                    onClick={() => showImage(i)}
                    src={`/${file.path}`}
                  />
                )
              })}
            </div>
          </div>
        </Fragment>
      )}

      <OverviewRow label="Ticket number" leftText={ticketId.toString()} renderRightContent={renderCasePriority} />
      {parentTicket && parentTicketId && (
        <OverviewRow
          label="Parent Ticket"
          onRightTextClick={() => navigate(`/tickets/${parentTicketId}`)}
          rightText={`#${parentTicketId} - ${parentTicket.subject}`}
          rightTextUnderline={true}
        />
      )}
      {externalSource &&
        externalId &&
        (externalSource === 'Airbnb' ? (
          <OverviewRow
            label="Airbnb Thread Id"
            renderRightContent={() => (
              <a
                className="font-bold underline underline-offset-2 text-link"
                href={`https://www.airbnb.com/hosting/messages/${externalId}`}
                rel="noreferrer"
                target="_blank"
              >
                {externalId}
              </a>
            )}
          />
        ) : (
          <OverviewRow label={`${externalSource} ID`} rightText={externalId} />
        ))}
      <OverviewRow
        label="Reservation"
        onRightTextClick={() => navigateToReservation(reservation?.id)}
        rightText={reservation?.id}
        rightTextUnderline={true}
      />
      <OverviewRow
        label="Home"
        onRightTextClick={() => navigateToHome(home?.id)}
        rightText={home?.searchDocument || ''}
        rightTextUnderline={true}
      />
      {!!home?.uninstallDate && (
        <OverviewRow
          label="Uninstall Date"
          rightText={formatDate(parseDate(home?.uninstallDate), 'MM/dd/yyyy') ?? ''}
        />
      )}
      {landingDirect && <OverviewRow label="Direct" renderRightContent={() => renderBooleanLabel(true)} />}
      {ticketType === Type.MAINTENANCE && (
        <OverviewRow label="Permission to enter" renderRightContent={() => renderBooleanLabel(!!permissionToEnter)} />
      )}
      <OverviewRow
        label="Account name"
        onRightTextClick={() => navigateToMember(contact?.id)}
        rightText={contact?.fullName || ''}
        rightTextUnderline={true}
      />
      <OverviewRow label="Market segment" renderRightContent={renderMarketSegment} />
      <OverviewRow
        label="Ticket owner"
        renderRightContent={() => (
          <OwnerSelection
            assignee={assignee}
            isTicketClosed={isTicketClosed}
            refetchDetails={refetchDetails}
            ticketId={ticketId}
          />
        )}
      />
      <OverviewRow
        label="Type"
        onRightTextClick={() => !isTicketClosed && modalToggle()}
        rightText={ticketType ?? ''}
        rightTextUnderline={true}
      />
      <OverviewRow
        label="Reason"
        renderRightContent={() => (
          <ReasonSelection
            isTicketClosed={isTicketClosed}
            refetchDetails={refetchDetails}
            ticketId={ticketId}
            ticketReason={ticketReason}
            ticketReasonId={ticketReasonId}
            ticketTypeId={ticketTypeId}
          />
        )}
      />
      {ticketHomeArea && <OverviewRow label="Home Area" rightText={ticketHomeArea} />}
      <OverviewRow
        label="Status"
        renderRightContent={() => (
          <TicketStatusSelection
            isTicketClosed={isTicketClosed}
            refetchDetails={refetchDetails}
            ticketId={ticketId}
            ticketStatusId={ticketStatusId}
          />
        )}
      />
      <OverviewRow label="Created by" rightText={creator?.fullName ?? ''} />
      <OverviewRow label="Created on" rightText={parseAndFormatDate(createdAt) ?? ''} />
      <OverviewRow className="mb-8" label="Closed on" rightText={closedAt ? parseAndFormatDate(closedAt) : ''} />

      {ticketData.allowAsParent && (
        <ChildTicketsSection
          childTickets={childTickets ?? []}
          contactId={contact?.id}
          homeId={home?.id}
          parentTicketId={ticketId}
          refetchDetails={refetchDetails}
          reservationId={reservation?.id}
        />
      )}

      {!!home?.propertyOnlinePortal && (
        <>
          <div className="mb-4">
            <p style={{ fontWeight: 700 }}>Property portal</p>
            <OverviewRow label="Username" rightText={home?.propertyPortalUsername ?? '---'} />
            <OverviewRow label="Password" rightText={home?.propertyPortalPassword ?? '---'} />
          </div>
          <div
            className="mb-4 flex h-10 w-full items-center justify-center font-bold text-link md:mb-0"
            style={{ fontWeight: 700 }}
          >
            <a href={home.propertyOnlinePortal} rel="noreferrer" target="_blank">
              Go to Property Portal{' '}
            </a>
          </div>
        </>
      )}

      {!!directPartnerUser && (
        <>
          <div className="mb-4">
            <p style={{ fontWeight: 700 }}>Direct Partner</p>
            <OverviewRow label="Name" rightText={directPartnerUser.fullName ?? '---'} />
            <OverviewRow label="Phone" rightText={directPartnerUser.phone ?? '---'} />
            <OverviewRow label="Email" rightText={directPartnerUser.email ?? '---'} />
          </div>
        </>
      )}
      {!isTicketClosed && (
        <Button className="w-full bg-grey-100 md:hidden" onClick={handleCloseTicket} variant="destructive">
          Close Ticket
        </Button>
      )}
      <BottomSheet header="Update Ticket Type" isVisible={modalShown} onClose={modalToggle}>
        <TypeSelection
          refetchDetails={refetchDetails}
          ticketId={ticketId}
          ticketType={ticketType}
          ticketTypeId={ticketTypeId}
          toggle={modalToggle}
        />
      </BottomSheet>
      <BottomSheet header="Update Description" isVisible={descriptionModalShown} onClose={descriptionModalToggle}>
        <DescriptionField
          description={description}
          refetchDetails={refetchDetails}
          ticketId={ticketId}
          toggle={descriptionModalToggle}
        />
      </BottomSheet>
      <div>
        <Lightbox
          close={() => setIsOpen(false)}
          index={photoIndex}
          open={isOpen}
          slides={images.map((img) => ({ src: `/${img.path}` }))}
        />
      </div>
    </SectionContainer>
  )
}
